import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Target, Star } from 'lucide-react';
import { Card } from '../ui/Card';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface GlanceBarProps {
  streakDays: number;
  dailyProgress: number;
  dailyGoalTarget: number;
  level: number;
  xp: number;
  onNavigateToModules?: () => void;
}
export function GlanceBar({
  streakDays,
  dailyProgress,
  dailyGoalTarget,
  level,
  xp,
  onNavigateToModules
}: GlanceBarProps) {
  const {
    isReducedMotion
  } = useMotionBudget();
  // Calculate progress percentages
  const streakProgress = Math.min(100, streakDays / 7 * 100);
  const dailyGoalProgress = Math.min(100, dailyProgress / dailyGoalTarget * 100);
  // XP progress within level (simplified: assume 1000 XP per level for display)
  const xpInCurrentLevel = xp % 1000;
  const levelProgress = Math.min(100, xpInCurrentLevel / 1000 * 100);
  const isDailyGoalReached = dailyProgress >= dailyGoalTarget;
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: -10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1
    }
  };
  return <motion.div variants={containerVariants} initial={isReducedMotion ? 'visible' : 'hidden'} animate="visible" className="mb-6" data-id="element-1260">
      <Card className="p-2 sm:p-3 bg-white border-2 border-slate-100 rounded-2xl shadow-sm flex items-center justify-between gap-2 sm:gap-4 overflow-hidden" data-id="element-1261">
        {/* Streak Segment */}
        <motion.button variants={itemVariants} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left group" data-id="element-1262">
          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1263">
            <Flame className={`w-4 h-4 sm:w-5 sm:h-5 ${streakDays > 0 ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-1264" />
            <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1265">
              {streakDays} {streakDays === 1 ? 'dag' : 'dagen'}
            </span>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1266">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${streakProgress}%`
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className={`h-full rounded-full ${streakDays > 0 ? 'bg-orange-500' : 'bg-slate-300'}`} data-id="element-1267" />
          </div>
        </motion.button>

        <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1268" />

        {/* Daily Goal Segment */}
        <motion.button variants={itemVariants} onClick={onNavigateToModules} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left group" data-id="element-1269">
          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1270">
            <Target className={`w-4 h-4 sm:w-5 sm:h-5 ${isDailyGoalReached ? 'text-green-500' : dailyProgress > 0 ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-1271" />
            <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1272">
              {dailyProgress}/{dailyGoalTarget} doel
            </span>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1273">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${dailyGoalProgress}%`
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.3
          }} className={`h-full rounded-full ${isDailyGoalReached ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-1274" />
          </div>
        </motion.button>

        <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1275" />

        {/* Level Segment */}
        <motion.div variants={itemVariants} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl text-left" data-id="element-1276">
          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1277">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" data-id="element-1278" />
            <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1279">
              Level {level}
            </span>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1280">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${levelProgress}%`
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.4
          }} className="h-full bg-blue-500 rounded-full" data-id="element-1281" />
          </div>
        </motion.div>
      </Card>
    </motion.div>;
}